import { styled } from "@mui/material";
import { IBreakpoints } from "../../types";
import { useHideGraphs } from "../hooks/useHideGraphs";
import { Colors } from "../util/Colors";
import { BlackDot, BLACK_DOT_WIDTH } from "./Dots";

const GRADIENT_BAR_WIDTH = 140;

const GradientBar = styled("div")({
    background: `linear-gradient(90deg, rgba(0, 202, 20, 0.88) 0%, rgba(0, 202, 20, 0.88) 26.56%, rgba(255, 166, 33, 0.88) 35.42%, rgba(255, 166, 33, 0.88) 65.1%, rgba(255, 98, 10, 0.88) 75%, rgba(255, 98, 10, 0.88) 100%)`,
    height: 12,
    minWidth: GRADIENT_BAR_WIDTH,
    display: "flex",
    alignItems: "center",
    position: "relative",
});

const ValueRange = styled("div")({
    fontSize: 14,
    fontWeight: 400,
    color: Colors.TEXT_LIGHT,
    marginRight: 16,
    minWidth: 120,
});

const getDotPosition = (breakpoints: IBreakpoints, value: number) => {
    if (value < breakpoints.yellow) {
        return 16.6;
    }
    if (value < breakpoints.red) {
        return 50;
    }
    return 83.3;
};

const getExerciseDropdownDotPosition = (exerciseDropdown: string) => {
    if (exerciseDropdown === "none") {
        // Middle of the 3rd bar in the gradient. Its a percentage, the width gets calculated in the Gradient component
        return 83.3;
    }
    if (exerciseDropdown === "lessThan2h5") {
        // Middle of the 2nd bar in the gradient
        return 50;
    }
    if (exerciseDropdown === "moreThan2h5") {
        // Middle of the 1st bar in the gradient
        return 16.6;
    }
    return 0;
};

const getSmokerDropdownDotPosition = (smokerDropdown: string) => {
    if (smokerDropdown === "nonSmoker") {
        return 12.5;
    }
    if (smokerDropdown === "exSmoker") {
        return 37.5;
    }
    if (smokerDropdown === "occasionalSmoker") {
        return 62.5;
    }
    if (smokerDropdown === "smoker") {
        return 87.5;
    }
    return 0;
};

const Gradient = ({
    valueRange,
    dotPosition,
    hideRange,
    valueAlignment = "center",
}: {
    valueRange?: string;
    dotPosition: number;
    hideRange?: boolean;
    valueAlignment?: "left" | "center" | "right";
}) => {
    const hideGraphs = useHideGraphs();

    return (
        <div style={{ display: "flex", flexDirection: hideGraphs ? "column" : "row", alignItems: "center" }}>
            {!hideRange && (
                <ValueRange
                    style={{
                        textAlign: valueAlignment,
                        marginRight: hideGraphs ? 0 : 16,
                        marginBottom: hideGraphs ? 4 : 0,
                    }}
                >
                    {valueRange}
                </ValueRange>
            )}
            <GradientBar>
                <BlackDot
                    style={{
                        position: "absolute",
                        left: dotPosition * ((GRADIENT_BAR_WIDTH - BLACK_DOT_WIDTH) / 100),
                    }}
                />
            </GradientBar>
        </div>
    );
};

export const ValueGradient = ({
    breakpoints,
    value,
    hideRange,
    exerciseDropdown,
    smokerDropdown,
    valueAlignment = "center",
}: {
    breakpoints?: IBreakpoints;
    value?: number;
    hideRange?: boolean;
    exerciseDropdown?: string;
    smokerDropdown?: string;
    valueAlignment?: "left" | "center" | "right";
}) => {
    if ((value && breakpoints) || exerciseDropdown || smokerDropdown) {
        return (
            <>
                {breakpoints && value && (
                    <Gradient
                        valueAlignment={valueAlignment}
                        valueRange={`${breakpoints.green} - ${breakpoints.red - 1}`}
                        hideRange={hideRange}
                        dotPosition={getDotPosition(breakpoints, value)}
                    />
                )}
                {exerciseDropdown && exerciseDropdown !== "notSpecified" && (
                    <Gradient
                        valueAlignment={valueAlignment}
                        dotPosition={getExerciseDropdownDotPosition(exerciseDropdown)}
                        hideRange={hideRange}
                    />
                )}
                {smokerDropdown && (
                    <Gradient
                        valueAlignment={valueAlignment}
                        dotPosition={getSmokerDropdownDotPosition(smokerDropdown)}
                        hideRange={hideRange}
                    />
                )}
            </>
        );
    }
    return null;
};
